import { memo } from 'react'
import { User } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { MarkdownPreview, NicolAIAvatar } from '@/shared/components'

export interface ChatMessageData {
  id: string
  role: 'assistant' | 'user'
  content: string
  createdAt: Date
}

interface ChatMessageProps {
  message: ChatMessageData
}

/**
 * Chat message component for NicolAI conversation
 * - NicolAI messages: left-aligned, max 900px, avatar with NicolAI image
 * - User messages: right-aligned, max 700px, avatar with User icon
 * Design reference: Pencil Node ID O1jef (NicolAI), uRz2O (User)
 */
export const ChatMessage = memo(function ChatMessage({ message }: ChatMessageProps) {
  const isAssistant = message.role === 'assistant'

  return (
    <div
      className={cn(
        'flex gap-3',
        isAssistant ? 'justify-start' : 'justify-end'
      )}
    >
      {/* Avatar - only for assistant */}
      {isAssistant && <NicolAIAvatar size="lg" />}

      {/* Message bubble */}
      <div
        className={cn(
          'rounded-2xl p-4',
          isAssistant
            ? 'max-w-[900px] rounded-tl-none border border-border bg-white'
            : 'max-w-[700px] rounded-tr-none bg-primary text-white'
        )}
      >
        {isAssistant ? (
          // NicolAI messages support markdown
          <MarkdownPreview
            content={message.content}
            className="text-sm [&_p]:text-foreground [&_p]:mb-2 [&_p:last-child]:mb-0"
          />
        ) : (
          // User messages are plain text (whitespace preserved)
          <p className="whitespace-pre-wrap text-sm">{message.content}</p>
        )}
      </div>

      {/* Avatar - only for user */}
      {!isAssistant && (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-foreground-muted">
          <User className="h-5 w-5 text-white" />
        </div>
      )}
    </div>
  )
})
